import type { DashboardSummary, Readiness, TranslationGapRow } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, CircleDot } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { StatusBadge } from '@/components/records';
import { Alert, Badge, Card, SkeletonRows } from '@/components/ui';
import { apiGet } from '@/lib/api';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const summary = useQuery({
    queryKey: qk.dashboard,
    queryFn: () => apiGet<DashboardSummary>('/admin/dashboard'),
  });

  const readiness = useQuery({
    queryKey: qk.readiness,
    queryFn: () => apiGet<Readiness>('/admin/readiness'),
  });

  const data = summary.data?.data;
  const checks = readiness.data?.data;

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        description={user ? t('dashboard.greeting', { name: user.displayName }) : undefined}
      />

      {summary.isLoading ? (
        <SkeletonRows rows={4} />
      ) : data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label={t('dashboard.categories')}
            value={data.menu.categories}
            note={t('dashboard.publishedOf', {
              published: data.menu.publishedCategories,
              total: data.menu.categories,
            })}
          />
          <Stat
            label={t('dashboard.items')}
            value={data.menu.items}
            note={t('dashboard.publishedOf', {
              published: data.menu.publishedItems,
              total: data.menu.items,
            })}
          />
          <Stat label={t('dashboard.offersActive')} value={data.offers.active} note={`${t('dashboard.offersUpcoming')}: ${data.offers.upcoming}`} />
          <Stat
            label={t('dashboard.branches')}
            value={data.branches.total}
            note={t('dashboard.publishedOf', {
              published: data.branches.published,
              total: data.branches.total,
            })}
          />
        </div>
      ) : (
        <Alert tone="danger">{t('common.somethingBroke')}</Alert>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card title={t('dashboard.gapsTitle')} description={t('dashboard.gapsIntro')}>
          {summary.isLoading ? (
            <SkeletonRows rows={3} />
          ) : data && data.translation.total === 0 ? (
            <p className="dio-meta">{t('dashboard.gapsEmpty')}</p>
          ) : data ? (
            <TranslationGaps translation={data.translation} />
          ) : null}
        </Card>

        <Card title={t('dashboard.readinessTitle')}>
          {readiness.isLoading ? (
            <SkeletonRows rows={4} />
          ) : checks ? (
            <>
              {checks.readyToLaunch ? (
                <Alert tone="success">{t('dashboard.readinessReady')}</Alert>
              ) : (
                <Alert tone="warning">
                  {t('dashboard.readinessBlocked', { count: checks.blockingIssues.length })}
                </Alert>
              )}

              <ul className="mt-3 flex flex-col gap-2">
                {checks.checks.map((check) => (
                  <li key={check.key} className="flex items-center gap-3">
                    {check.ok ? (
                      <Check className="dio-icon" style={{ color: 'var(--color-success-fg)' }} aria-hidden="true" />
                    ) : check.blocking ? (
                      <AlertTriangle className="dio-icon" style={{ color: 'var(--color-danger-fg)' }} aria-hidden="true" />
                    ) : (
                      <CircleDot className="dio-icon" style={{ color: 'var(--color-warning-fg)' }} aria-hidden="true" />
                    )}
                    <span className="flex-1">{t(`readiness.${check.key}`)}</span>
                    <Badge tone={check.ok ? 'success' : check.blocking ? 'danger' : 'warning'}>
                      {check.ok
                        ? t('dashboard.readinessDone')
                        : check.blocking
                          ? t('dashboard.readinessBlocking')
                          : t('dashboard.readinessWarning')}
                    </Badge>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Card>
      </div>
    </>
  );
}

function Stat({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="dio-stat">
      <p className="dio-stat__label">{label}</p>
      <p className="dio-stat__value tabular">{value}</p>
      {note && <p className="dio-meta">{note}</p>}
    </div>
  );
}

const GAP_SECTIONS = [
  { key: 'categories', to: '/menu/categories' },
  { key: 'items', to: '/menu/items' },
  { key: 'addonGroups', to: '/menu/addons' },
  { key: 'offers', to: '/offers' },
  { key: 'branches', to: '/branches' },
  { key: 'faqs', to: '/content/faqs' },
] as const;

function TranslationGaps({ translation }: { translation: DashboardSummary['translation'] }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      {GAP_SECTIONS.map((section) => {
        const rows = translation[section.key] as TranslationGapRow[];
        if (rows.length === 0) return null;

        return (
          <div key={section.key}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <Link to={section.to} className="font-medium">
                {t(`nav.${section.key === 'addonGroups' ? 'addons' : section.key}`)}
              </Link>
              <span className="dio-meta tabular">{t('dashboard.gapsCount', { count: rows.length })}</span>
            </div>

            <ul className="flex flex-col gap-2">
              {rows.slice(0, 5).map((row) => (
                <li key={row.id} className="flex flex-wrap items-center gap-2">
                  {/* Whichever language is present is the one shown, in its own
                      script — never substituted from the other side. */}
                  <span lang={row.name.ar ? 'ar' : 'en'} dir={row.name.ar ? 'rtl' : 'ltr'}>
                    {row.name.ar || row.name.en || '—'}
                  </span>
                  <StatusBadge status={row.publicationStatus} />
                  {row.missing.ar && <Badge tone="warning">{t('bilingual.arabic')}</Badge>}
                  {row.missing.en && <Badge tone="warning">{t('bilingual.english')}</Badge>}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
